import config, {Brand} from '../../config';
import {universal} from '../../design-system/tokens';
import theme from '../../style/theme';

/**
 * Per-brand header colours (DIS-1840), keyed on `config.BASE_TENANT`.
 *
 * Every colour but `onGround` comes from the brand theme (assets/common/theme/{brand}/theme.js):
 * - ground: `colour.background.general.primary`, as the footer (DIS-1841) decided. TC keeps
 *   the theme #A0CE66 over Figma "Thirsty Camel/Green" #ACCA57.
 * - divider, cartChip, onCartChip: `colour.header.*`, seeded from the header prototype.
 * - accent: `colour.text.heading.primary`.
 * - brandOnWhite: `colour.text.heading.primary`, as accent, but a separate key (G7-D2).
 * `onGround` (text and icons on the ground) is the per-brand map `onGroundByBrand` in this
 * file, not a theme value: Figma `Universal/*` white or black (`universal`).
 */
export type HeaderChrome = {
  /** Header ground. */
  ground: string;
  /** Text and icons on the ground. */
  onGround: string;
  /** Row dividers. */
  divider: string;
  /** Cart chip fill. */
  cartChip: string;
  /** Text and icon on the cart chip. */
  onCartChip: string;
  /** Open nav item, dropdown main links and "Shop All": the brand heading colour. */
  accent: string;
  /**
   * Brand-coloured text on a white surface: the avatar initials and the account greeting.
   * Not `colour.general.primary`, which is 1.35:1 (SNS #FFDD00) and 1.82:1 (TC #A0CE66) on
   * white. The heading colour is 7.45:1 (BM, SNS #0054A6) and 21:1 (HB, TC #000000).
   */
  brandOnWhite: string;
};

const onGroundByBrand: Record<Exclude<Brand, Brand.DEVELOPMENT>, string> = {
  [Brand.BOTTLEMART]: universal.white,
  [Brand.SIPNSAVE]: universal.black,
  [Brand.HARRY_BROWN]: universal.white,
  [Brand.THIRSTY_CAMEL]: universal.black,
};

export const headerChrome: HeaderChrome = {
  ground: theme.colour.background.general.primary,
  onGround:
    onGroundByBrand[config.BASE_TENANT as keyof typeof onGroundByBrand] ??
    onGroundByBrand[Brand.BOTTLEMART],
  divider: theme.colour.header.divider,
  cartChip: theme.colour.header.cartChip,
  onCartChip: theme.colour.header.onCartChip,
  accent: theme.colour.text.heading.primary,
  brandOnWhite: theme.colour.text.heading.primary,
};
